import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import ResumeUpload from './ResumeUpload';
import Form1Sections from './Form1Sections';
import { Button, Field } from '../ui';
import FormSection from './FormSection';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Documents the candidate may attach. [files key, locale key, Indonesian
// fallback, accept]. Apply.jsx maps each key to its storage prefix and
// form_data field.
const DOCS = {
  resume: ['resume', 'resume', 'Resume/CV', '.pdf,.doc,.docx'],
  idCard: ['idCard', 'idCard', 'KTP', '.jpg,.jpeg,.png,.pdf'],
  photo: ['photo', 'photo', 'Foto Terbaru', '.jpg,.jpeg,.png'],
  familyCard: ['familyCard', 'docFamilyCard', 'Kartu Keluarga (KK)', '.jpg,.jpeg,.png,.pdf'],
  driverLicense: ['driverLicense', 'docDriverLicense', 'SIM', '.jpg,.jpeg,.png,.pdf'],
  diploma: ['diploma', 'docDiploma', 'Ijazah', '.jpg,.jpeg,.png,.pdf'],
  healthCert: ['healthCert', 'docHealthCert', 'Surat Keterangan Sehat', '.jpg,.jpeg,.png,.pdf'],
  skck: ['skck', 'docSkck', 'SKCK', '.jpg,.jpeg,.png,.pdf'],
  refLetter: ['refLetter', 'docRefLetter', 'Surat Pengalaman Kerja', '.jpg,.jpeg,.png,.pdf'],
};

// Which documents are wajib is now per-posting: HR ticks them per job in the
// admin panel (job_positions.required_documents). A driver's SIM is mandatory
// for a driver and pointless for a dishwasher, so one global list could never
// be right for both.
//
// Used when a posting has no list of its own — every posting created before
// HR could set this, and any they leave alone. Keeping the pre-existing set
// here means nothing changes for them.
export const DEFAULT_REQUIRED_DOC_KEYS = ['resume', 'idCard', 'photo', 'familyCard', 'healthCert', 'skck'];

// The order boxes are drawn in, whichever group they land in. Splitting this
// list into wajib/opsional is the per-posting part.
const DOC_ORDER = [
  'resume', 'idCard', 'photo', 'familyCard', 'healthCert', 'skck',
  'driverLicense', 'diploma', 'refLetter',
];

// Shifts a posting can offer. These keys are stored on the applicant as
// preferred_shift and read back by the admin's applicant filter and resume
// export (both do t(`apply.${key}`)), so the set is fixed — a posting picks
// from it, it cannot invent a shift name. Free-text scheduling detail already
// has a home in job_positions.schedule_notes.
const ALL_SHIFTS = ['morning', 'evening', 'night', 'flexible'];

export default function ApplicationForm({ onSubmit, submitting, hasQuestionnaire = false, defaultValues, requiredDocKeys, availableShifts }) {
  const { t } = useTranslation();
  const { register, handleSubmit, watch, formState: { errors } } = useForm({ defaultValues });

  // Unlike the documents, an empty list falls back to all four rather than
  // being honoured: a posting offering no shift at all would render an empty
  // dropdown, which is a mistake every time rather than an intent.
  const shiftOptions = Array.isArray(availableShifts) && availableShifts.some((s) => ALL_SHIFTS.includes(s))
    ? ALL_SHIFTS.filter((s) => availableShifts.includes(s))
    : ALL_SHIFTS;

  // An array (even an empty one) is HR's explicit choice and is honoured as
  // given; only a missing/malformed value falls back to the defaults. Unknown
  // keys are dropped so a stale posting can't demand a document that no
  // longer has an upload box.
  const requiredKeys = new Set(
    Array.isArray(requiredDocKeys)
      ? requiredDocKeys.filter((k) => DOCS[k])
      : DEFAULT_REQUIRED_DOC_KEYS
  );
  const requiredDocs = DOC_ORDER.filter((k) => requiredKeys.has(k)).map((k) => DOCS[k]);
  const optionalDocs = DOC_ORDER.filter((k) => !requiredKeys.has(k)).map((k) => DOCS[k]);
  const [files, setFiles] = useState({
    quranVideo: null,
    ...Object.fromEntries(Object.keys(DOCS).map((key) => [key, null])),
  });
  const [fileError, setFileError] = useState('');

  const handleFormSubmit = (data) => {
    // Name what is actually missing — "please attach your documents" sends
    // the candidate hunting through nine upload boxes.
    const missing = requiredDocs.filter(([key]) => !files[key]);
    if (missing.length) {
      const names = missing.map(([, localeKey, fallback]) => t(`apply.${localeKey}`, fallback)).join(', ');
      setFileError(t('apply.docsRequired', { docs: names, defaultValue: 'Dokumen wajib belum dilampirkan: {{docs}}' }));
      document.getElementById('required-documents')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setFileError('');
    onSubmit(data, files);
  };

  const handleFileChange = (type, file) => {
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8" noValidate>
      {/* Personal Information */}
      <FormSection index={1} icon="user" title={t('apply.personalInfo')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="fullName" label={t('apply.fullName')} required error={errors.fullName?.message} className="md:col-span-2">
            <input
              id="fullName"
              type="text"
              {...register('fullName', { required: t('apply.fieldRequired', 'This field is required') })}
              aria-invalid={!!errors.fullName}
              className="input-field"
            />
          </Field>

          <Field id="phone" label={t('apply.phone')} required error={errors.phone?.message}>
            <input
              id="phone"
              type="tel"
              {...register('phone', {
                required: t('apply.fieldRequired', 'This field is required'),
                pattern: {
                  value: /^[+]?[\d\s()-]{7,}$/,
                  message: t('apply.phoneInvalid', 'Enter a valid phone number'),
                },
              })}
              placeholder="+62..."
              aria-invalid={!!errors.phone}
              className="input-field"
            />
          </Field>

          <Field id="email" label={t('apply.email')} error={errors.email?.message}>
            <input
              id="email"
              type="email"
              {...register('email', {
                pattern: {
                  value: EMAIL_PATTERN,
                  message: t('apply.emailInvalid', 'Enter a valid email address'),
                },
              })}
              aria-invalid={!!errors.email}
              className="input-field"
            />
          </Field>

          <Field id="dateOfBirth" label={t('apply.dateOfBirth')}>
            <input
              id="dateOfBirth"
              type="date"
              {...register('dateOfBirth')}
              className="input-field"
            />
          </Field>

          <Field id="gender" label={t('apply.gender')}>
            <select id="gender" {...register('gender')} className="input-field">
              <option value="">-</option>
              <option value="male">{t('apply.male')}</option>
              <option value="female">{t('apply.female')}</option>
            </select>
          </Field>

          <Field id="maritalStatus" label={t('apply.maritalStatus')}>
            <select id="maritalStatus" {...register('maritalStatus')} className="input-field">
              <option value="">-</option>
              <option value="single">{t('apply.single')}</option>
              <option value="married">{t('apply.married')}</option>
              <option value="divorced">{t('apply.divorced')}</option>
              <option value="widowed">{t('apply.widowed')}</option>
            </select>
          </Field>
        </div>
      </FormSection>

      {/* Location */}
      <FormSection index={2} icon="compass" title={t('apply.location')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="currentCity" label={t('apply.currentCity')}>
            <input
              id="currentCity"
              type="text"
              {...register('currentCity')}
              className="input-field"
            />
          </Field>

          <Field id="province" label={t('apply.province')}>
            <input
              id="province"
              type="text"
              {...register('province')}
              className="input-field"
            />
          </Field>

          <Field id="currentAddress" label={t('apply.currentAddress')} className="md:col-span-2">
            <textarea
              id="currentAddress"
              {...register('currentAddress')}
              rows={2}
              className="input-field"
            />
          </Field>
        </div>
      </FormSection>

      {/* Availability */}
      <FormSection index={3} icon="calendarClock" title={t('apply.availability')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field id="availableStartDate" label={t('apply.availableStartDate')}>
            <input
              id="availableStartDate"
              type="date"
              {...register('availableStartDate')}
              className="input-field"
            />
          </Field>

          <Field id="preferredShift" label={t('apply.preferredShift')}>
            <select id="preferredShift" {...register('preferredShift')} className="input-field">
              <option value="">-</option>
              {shiftOptions.map((shift) => (
                <option key={shift} value={shift}>{t(`apply.${shift}`)}</option>
              ))}
            </select>
          </Field>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="willingToRelocate"
              {...register('willingToRelocate')}
              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <label htmlFor="willingToRelocate" className="text-gray-700">
              {t('apply.willingToRelocate')}
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="willingForDorm"
              {...register('willingForDorm')}
              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <label htmlFor="willingForDorm" className="text-gray-700">
              {t('apply.willingForDorm')}
            </label>
          </div>
        </div>
      </FormSection>

      {/* FORM 1 — Pendaftaran & Screening Awal (BRS-REC-WNA-001) */}
      <Form1Sections
        register={register}
        watch={watch}
        errors={errors}
        onQuranVideo={(file) => handleFileChange('quranVideo', file)}
      />

      {/* Documents */}
      <FormSection
        index={12}
        icon="folder"
        title={t('apply.documents')}
        description={requiredDocs.length
          ? t('apply.requiredDocsNote', 'Wajib dilampirkan — lamaran tidak dapat dikirim tanpa dokumen ini.')
          : undefined}
        className="scroll-mt-24"
        id="required-documents"
      >
        {/* A posting may mark nothing as wajib, in which case the whole
            required block drops out rather than rendering an empty grid. */}
        {requiredDocs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {requiredDocs.map(([key, localeKey, fallback, accept]) => (
              <ResumeUpload
                key={key}
                label={`${t(`apply.${localeKey}`, fallback)} *`}
                accept={accept}
                onChange={(file) => handleFileChange(key, file)}
              />
            ))}
          </div>
        )}
        {fileError && <p className="mt-2 text-sm text-red-600" role="alert">{fileError}</p>}

        {/* Supporting documents — all optional. Kept in their own group so a
            candidate who has none of them isn't scared off by six empty
            upload boxes sitting under the required ones. */}
        {optionalDocs.length > 0 && (
        <div className={requiredDocs.length ? 'mt-8' : ''}>
          <h4 className="text-base font-medium text-gray-900">
            {t('apply.supportingDocs', 'Dokumen Pendukung')}
          </h4>
          <p className="mt-1 mb-4 text-sm text-gray-500">
            {t('apply.supportingDocsNote', 'Semuanya opsional — unggah hanya yang Anda miliki. Lamaran tetap dapat dikirim tanpa dokumen ini.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {optionalDocs.map(([key, localeKey, fallback, accept]) => (
              <ResumeUpload
                key={key}
                label={t(`apply.${localeKey}`, fallback)}
                accept={accept}
                onChange={(file) => handleFileChange(key, file)}
              />
            ))}
          </div>
        </div>
        )}
      </FormSection>

      {/* Submit — advances to the questionnaire when one exists, otherwise submits */}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full disabled:opacity-50"
      >
        {submitting
          ? t('apply.submitting')
          : hasQuestionnaire
            ? t('apply.next')
            : t('apply.submit')}
        {!submitting && hasQuestionnaire && (
          <svg className="w-5 h-5 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        )}
      </Button>
    </form>
  );
}
